import _ from 'underscore';
import type { DataRefId, OverreactRequestShape } from '../../public-types';
import { Subject } from '../../utils/observer-pattern';

export interface DataRefStatus<TVariables extends object = object> {
  previousVariables?: TVariables;
  lastUpdateTimestamp: number;
  error?: unknown;
}

export interface ComponentRegistration {
  componentName: string;
}

export class DataRef<TVariables extends object = object> extends Subject {
  UiComponents: string[];
  cursor: Record<string, unknown>;
  idRefs: string[];
  key: DataRefId;
  status: DataRefStatus<TVariables>;

  constructor(key: DataRefId) {
    super();
    this.key = key;
    this.idRefs = [];
    this.status = {
      previousVariables: undefined,
      lastUpdateTimestamp: Date.now(),
      error: undefined,
    };
    this.cursor = {};
    this.UiComponents = [];

    this.includes = this.includes.bind(this);
    this.push = this.push.bind(this);
    this.add = this.add.bind(this);
    this.delete = this.delete.bind(this);
    this.onUpdate = this.onUpdate.bind(this);
    this.updateStatus = this.updateStatus.bind(this);
    this.onError = this.onError.bind(this);
    this.clearError = this.clearError.bind(this);
    this.onEntitiesCreated = this.onEntitiesCreated.bind(this);
    this.onEntitiesDeleted = this.onEntitiesDeleted.bind(this);
    this.reset = this.reset.bind(this);
  }

  registerComponent({ componentName }: ComponentRegistration) {
    this.UiComponents = _.uniq([...this.UiComponents, componentName]);
    this.updateDevTools();
  }

  unregisterComponent({ componentName }: ComponentRegistration) {
    this.UiComponents = _.without(this.UiComponents, componentName);
    this.updateDevTools();
  }

  updateDevTools() {
    if (window.__OVERREACT_DEVTOOLS__) {
      window.__OVERREACT_DEVTOOLS__.onDataRefChange({
        components: this.UiComponents,
        key: this.key,
        idRefs: this.idRefs,
      });
    }
  }

  includes(id: string) {
    return this.idRefs.includes(id);
  }

  push(id: string) {
    return this.idRefs.push(id);
  }

  add(id: string) {
    if (!this.includes(id)) {
      this.push(id);
    }
  }

  delete(ids: string[]) {
    if (_.intersection(this.idRefs, ids).length > 0) {
      this.idRefs = _.difference(this.idRefs, ids);
    }
  }

  onError(error: unknown, ...args: unknown[]) {
    this.status.error = error;
    this.notify('onError', error, ...args);
  }

  clearError() {
    this.onError(undefined);
  }

  clear() {
    this.idRefs = [];
    this.clearError();
  }

  reset(ids: string[]) {
    this.idRefs = ids;
    this.notify('update', ids);
  }

  updateStatus(newStatus: Partial<DataRefStatus<TVariables>>) {
    this.status = { ...this.status, ...newStatus };
  }

  onUpdate(updatedIds: string[], request?: OverreactRequestShape) {
    if (_.intersection(this.idRefs, updatedIds).length > 0) {
      this.notify('update', updatedIds, request);
    }
  }

  onEntitiesCreated(ids: string[]) {
    if (!_.isEmpty(ids)) {
      this.notify('onEntitiesCreated', ids);
    }
  }

  onEntitiesDeleted(ids: string[]) {
    if (_.intersection(this.idRefs, ids).length > 0) {
      this.delete(ids);
      this.notify('update', this.idRefs);
    }
  }

  toJson() {
    return { key: this.key, idRefs: this.idRefs, status: this.status };
  }
}
